import { useTranslation } from "react-i18next";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { useForm } from "react-hook-form";
import {
  Box, Button,
  chakra, Flex, Heading, IconButton, Radio, RadioGroup, Stack, Text, Tooltip, useDisclosure, useToast
} from "@chakra-ui/react";
import { EsgLabel, FormSelect, FormTextArea } from "../../../../components/Forms";
import { Answer } from "../../../SupplierPhysicalRiskQuestionnaire/components/PhysicalRiskAssessmentForm/components";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { FileAttachment } from "../../../OrganizationQuestionnaire/components/FileAttachment";
import {
  supplierQuestionnaireKeys,
  useAttachDecarbonizationPlanDocumentation,
  useAttachDeforestationPlanDocumentation,
  useAttachLatestSustainabilityReport,
  useCreateSupplierDecarbonizationPlanQuestionnaire,
  useDefaultSupplierQuestionnaireConfig, useDeleteDecarbAttachment,
  useFinalizeSupplierQuestionnaire, useGetDecarbAttachment,
  useUpdateSupplierDecarbonizationPlanQuestionnaire
} from "../../../../hooks/query";
import { FaQuestionCircle } from "react-icons/fa";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import { StatusCodes } from "http-status-codes";

const mapApiDataToFormData = (apiData) => {
  const formData = {};

  if (apiData) {
    formData["futurePlans"] = apiData.futurePlans ?? "";
    formData["futurePlansClarification"] = apiData.futurePlansClarification ?? "";
  }

  return formData;
};

const mapFormDataToApiData = (formData) => {
  return {
    futurePlans: formData.futurePlans !== "" ? formData.futurePlans : null,
    futurePlansClarification: formData.futurePlansClarification
  };
};

function DecarbonizationNetZeroPlan({
                                      data,
                                      isPreview,
                                      returnToList,
                                      returnToListRequested,
                                      declineReturnToListRequest
                                    }) {
  const { id: questionnaireId, decarbonizationPlan } = data;

  const formDefaultValues = mapApiDataToFormData(decarbonizationPlan);

  const { t } = useTranslation("translation", {
    keyPrefix: "supplierQuestionnaire.decarbonizationNetZeroPlan"
  });
  const { t: globalTranslation } = useTranslation("translation");
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [netZeroTargetSet, setNetZeroTargetSet] = useState(null);
  const [sbtiTargetsSet, setSbtiTargetsSet] = useState(null);
  const [decarbonizationAttachment, setDecarbonizationAttachment] = useState(null);
  const [sustainabilityAttachment, setSustainabilityAttachment] = useState(null);
  const [deforestationAttachment, setDeforestationAttachment] = useState(null);

  const [isInitialState, setIsInitialState] = useState(true);
  const [finalizeQuestionnaire, setFinalizeQuestionnaire] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [netZeroTargetErrMsg, setNetZeroTargetErrMsg] = useState("");
  const [sbtiTargetsErrMsg, setSbtiTargetsErrMsg] = useState("");
  const [netZeroPlanErrMsg, setNetZeroPlanErrMsg] = useState("");

  const netZeroTargetErrMsgRef = useRef();
  const sbtiTargetsErrMsgRef = useRef();
  const netZeroPlanErrMsgRef = useRef();

  const {
    register,
    handleSubmit,
    formState,
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: formDefaultValues
  });

  const { mutate: finalize } = useFinalizeSupplierQuestionnaire({
    ...useDefaultSupplierQuestionnaireConfig(),
    onSuccess: () => {
      showSuccessToast(toast, globalTranslation("supplierQuestionnaire.page.finalized"));
      returnToList();
    },
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
      setFinalizeQuestionnaire(false);
    }
  });

  useEffect(() => {
    if ((formState.errors && Object.keys(formState.errors).length > 0) ||
      netZeroTargetErrMsg !== "" || sbtiTargetsErrMsg !== "" || netZeroPlanErrMsg !== "") {
      if (finalizeQuestionnaire === true) {
        setFinalizeQuestionnaire(false);
      }
      if (returnToListRequested === true) {
        declineReturnToListRequest();
      }
    }
    if (formState.isDirty !== isDirty) {
      // Workaround that guarantees the actual value of isDirty attribute is used
      setIsDirty(formState.isDirty);
    }
  }, [formState, finalizeQuestionnaire, returnToListRequested, declineReturnToListRequest, isDirty,
    netZeroTargetErrMsg, sbtiTargetsErrMsg, netZeroPlanErrMsg]);

  useEffect(() => {
    if (returnToListRequested === true) {
      const decarbForm = document.getElementById("decarb-net-zero-plan-form");
      if (decarbForm) {
        decarbForm.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
      }
    }
  }, [returnToListRequested]);

  useEffect(() => {
    if (isInitialState && data.decarbonizationPlan) {
      const {
        netZeroTargetSet, sbtiTargetsSet,
        decarbonizationPlanDocumentation, decarbonizationPlanDocumentationAttachmentId,
        latestSustainabilityReport, latestSustainabilityReportAttachmentId,
        deforestationPlanDocumentation, deforestationPlanDocumentationAttachmentId
      } = data.decarbonizationPlan;

      setNetZeroTargetSet(typeof netZeroTargetSet !== "undefined" ?
        netZeroTargetSet === true ? "true" : "false" : null);
      setSbtiTargetsSet(typeof sbtiTargetsSet !== "undefined" ?
        sbtiTargetsSet === true ? "true" : "false" : null);

      if (typeof decarbonizationPlanDocumentation !== "undefined") {
        setDecarbonizationAttachment({
          attachmentName: decarbonizationPlanDocumentation,
          attachmentId: decarbonizationPlanDocumentationAttachmentId
        });
        setNetZeroPlanErrMsg("");
      } else {
        setDecarbonizationAttachment(null);
      }
      if (typeof latestSustainabilityReport !== "undefined") {
        setSustainabilityAttachment({
          attachmentName: latestSustainabilityReport,
          attachmentId: latestSustainabilityReportAttachmentId
        });
      } else {
        setSustainabilityAttachment(null);
      }
      if (typeof deforestationPlanDocumentation !== "undefined") {
        setDeforestationAttachment({
          attachmentName: deforestationPlanDocumentation,
          attachmentId: deforestationPlanDocumentationAttachmentId
        });
      } else {
        setDeforestationAttachment(null);
      }

      setIsInitialState(false);
    }
  }, [isInitialState, data]);

  const queryConfig = {
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      if (decarbonizationAttachment !== null && netZeroTargetSet !== "true") {
        deleteAttachment({
          attachmentId: decarbonizationAttachment.attachmentId,
          questionnaireId: questionnaireId
        });
      } else {
        handleSuccessfulSave();
      }
    },
    onError: err => showErrorToast(toast, err.message),
    onSettled: () => dispatch(hideAppSpinner())
  };

  const { mutate: create } = useCreateSupplierDecarbonizationPlanQuestionnaire(queryConfig);
  const { mutate: update } = useUpdateSupplierDecarbonizationPlanQuestionnaire(queryConfig);

  const checkForChanges = (apiData) => {
    const attributes = ["netZeroTargetSet", "sbtiTargetsSet", "decarbonizationPlan",
      "decarbonizationPlanAttachmentId", "latestSustainabilityReport", "latestSustainabilityReportAttachmentId",
      "deforestationPlanDocumentation", "deforestationPlanDocumentationAttachmentId"];

    return attributes
      .map(el => apiData?.[el] !== decarbonizationPlan?.[el])
      .filter(el => el === true);
  };

  const onSubmit = (data) => {
    if (!isPreview) {
      if (!netZeroTargetSet) {
        setNetZeroTargetErrMsg(globalTranslation("errors.required"));
        netZeroTargetErrMsgRef?.current?.scrollIntoView();
      } else if (netZeroTargetSet && netZeroTargetSet === "true" &&
        (!decarbonizationAttachment || Object.keys(decarbonizationAttachment)?.length === 0 ||
          decarbonizationAttachment?.attachmentName === "")) {
        setNetZeroPlanErrMsg(globalTranslation("errors.required"));
        netZeroPlanErrMsgRef?.current?.scrollIntoView();
      } else if (netZeroTargetSet && netZeroTargetSet === "true" && !sbtiTargetsSet) {
        setSbtiTargetsErrMsg(globalTranslation("errors.required"));
        sbtiTargetsErrMsgRef?.current?.scrollIntoView();
      } else {
        const apiData = mapFormDataToApiData(data);
        apiData.netZeroTargetSet = netZeroTargetSet === "true";
        apiData.decarbonizationPlanDocumentation = decarbonizationAttachment?.attachmentName;
        apiData.decarbonizationPlanDocumentationAttachmentId = decarbonizationAttachment?.attachmentId;
        apiData.sbtiTargetsSet = sbtiTargetsSet === "true";
        apiData.latestSustainabilityReport = sustainabilityAttachment?.attachmentName;
        apiData.latestSustainabilityReportAttachmentId = sustainabilityAttachment?.attachmentId;
        apiData.deforestationPlanDocumentation = deforestationAttachment?.attachmentName;
        apiData.deforestationPlanDocumentationAttachmentId = deforestationAttachment?.attachmentId;

        if (isDirty || checkForChanges(apiData).length > 0) {
          if (decarbonizationPlan?.id) {
            update({ questionnaireId, id: decarbonizationPlan.id, data: apiData });
          } else {
            create({ questionnaireId, data: apiData });
          }
          reset(data);
        } else {
          return finalizeQuestionnaire ? finalize(questionnaireId) :
            returnToListRequested === true ? returnToList() : null;
        }
      }
    }
  };

  const futurePlans = watch("futurePlans");

  const handleFinalizeConfirmation = () => {
    handleSubmit(onSubmit)();
    handleDialogClose(true);
  };

  const handleDialogClose = (confirmed) => {
    if (confirmed !== true) {
      setFinalizeQuestionnaire(false);
    }
    onConfirmDialogClose();
  };

  const FUTURE_PLANS = {
    "IN_DEVELOPMENT": "IN_DEVELOPMENT",
    "NOT_STARTED": "NOT_STARTED",
    "OTHER": "OTHER"
  };

  const { mutate: deleteAttachment } = useDeleteDecarbAttachment({
    onMutate: () => dispatch(showAppSpinner()),
    onSuccess: () => {
      setDecarbonizationAttachment(null);
      handleSuccessfulSave();
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => dispatch(hideAppSpinner())
  });

  const handleSuccessfulSave = () => {
    if (finalizeQuestionnaire) {
      finalize(questionnaireId);
    } else {
      showSuccessToast(toast, globalTranslation("entitySaved", { entityName: t("title") }));
      queryClient.invalidateQueries(supplierQuestionnaireKeys.supplierQuestionnaireById());
      if (returnToListRequested === true) {
        returnToList();
      }
    }
  };

  return (
    <chakra.form id="decarb-net-zero-plan-form" onSubmit={handleSubmit(onSubmit)}>
      <Flex flexFlow="column" gap={10}>
        <Box>
          <Heading as="h2" size="md" marginBlock="1">
            {t("introduction")}
          </Heading>
          <Text opacity="0.75" fontSize="sm">
            {t(data?.organizationName?.toLowerCase() === "howdens" ?
                "howdensIntroductionText" : "genericIntroductionText",
              { sourcingCompany: data?.organizationName })
            }
          </Text>
        </Box>

        <Box>
          <EsgLabel text={`${t("netZeroTargetSet")} *`} />
          {isPreview ?
            <Answer answerText={netZeroTargetSet === "true" ? t("inputs.yes") : t("inputs.no")} /> :
            <>
              <RadioGroup value={netZeroTargetSet} style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value="true"
                    onChange={e => {
                      if (netZeroTargetErrMsg !== "") {
                        setNetZeroTargetErrMsg("");
                      }
                      setValue("futurePlans", "");
                      setValue("futurePlansClarification", "");
                      setNetZeroTargetSet(e?.target.value);
                    }}
                  >
                    {t("inputs.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value="false"
                    onChange={e => {
                      if (netZeroTargetErrMsg !== "") {
                        setNetZeroTargetErrMsg("");
                      }
                      if (sbtiTargetsErrMsg !== "") {
                        setSbtiTargetsErrMsg("");
                      }
                      if (netZeroPlanErrMsg !== "") {
                        setNetZeroPlanErrMsg("");
                      }
                      setSbtiTargetsSet(null);
                      setNetZeroTargetSet(e?.target.value);
                    }}
                  >
                    {t("inputs.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
              {netZeroTargetErrMsg && netZeroTargetErrMsg !== "" ?
                <Box
                  ref={netZeroTargetErrMsgRef}
                  display="flex"
                  alignItems="center"
                  color="red.500"
                  marginTop="2"
                  fontSize="sm"
                  lineHeight="normal"
                >
                  {netZeroTargetErrMsg}
                </Box> : null}
            </>
          }
        </Box>

        {netZeroTargetSet && netZeroTargetSet === "true" ? <Box>
          <EsgLabel text={`${t("uploadPlan")}: *`} />
          <FileAttachment
            type={"decarbonization-net-zero-plan"}
            uploadHook={useAttachDecarbonizationPlanDocumentation}
            downloadHook={useGetDecarbAttachment}
            deleteHook={useDeleteDecarbAttachment}
            invalidationKey={supplierQuestionnaireKeys.supplierQuestionnaireById()}
            currentAttachment={decarbonizationAttachment}
            params={{
              questionnaireId: questionnaireId
            }}
            isDisabled={isPreview}
            tooltip={t("fileAttachmentTooltip")}
            onSuccessHandler={(data) => {
              if (data !== null && typeof data === "object") {
                if (data.status === StatusCodes.NO_CONTENT) {
                  // Handling the case of successful file delete
                  setDecarbonizationAttachment(null);
                } else {
                  // Handling the case of successful file upload
                  setDecarbonizationAttachment({
                    attachmentName: data.name,
                    attachmentId: data.documentId
                  });
                  setNetZeroPlanErrMsg("");
                }
              }
            }}
          />
          {netZeroPlanErrMsg !== "" ?
            <Box
              ref={netZeroPlanErrMsgRef}
              display="flex"
              alignItems="center"
              color="red.500"
              marginTop="2"
              fontSize="sm"
              lineHeight="normal"
            >
              {netZeroPlanErrMsg}
            </Box> : null}
        </Box> : null}

        {netZeroTargetSet && netZeroTargetSet === "true" ? <Box>
          <EsgLabel text={`${t("sbtiTargetsSet")} *`} />
          {isPreview ?
            <Answer answerText={sbtiTargetsSet === "true" ? t("inputs.yes") : t("inputs.no")} /> :
            <>
              <RadioGroup value={sbtiTargetsSet} style={{ position: "relative" }}>
                <Stack spacing={5} direction="row">
                  <Radio
                    colorScheme="customScheme"
                    value="true"
                    onChange={e => {
                      if (sbtiTargetsErrMsg !== "") {
                        setSbtiTargetsErrMsg("");
                      }
                      setSbtiTargetsSet(e?.target.value);
                    }}
                  >
                    {t("inputs.yes")}
                  </Radio>
                  <Radio
                    colorScheme="customScheme"
                    value="false"
                    onChange={e => {
                      if (sbtiTargetsErrMsg !== "") {
                        setSbtiTargetsErrMsg("");
                      }
                      setSbtiTargetsSet(e?.target.value);
                    }}
                  >
                    {t("inputs.no")}
                  </Radio>
                </Stack>
              </RadioGroup>
              {sbtiTargetsErrMsg && sbtiTargetsErrMsg !== "" ?
                <Box
                  ref={sbtiTargetsErrMsgRef}
                  display="flex"
                  alignItems="center"
                  color="red.500"
                  marginTop="2"
                  fontSize="sm"
                  lineHeight="normal"
                >
                  {sbtiTargetsErrMsg}
                </Box> : null}
            </>
          }
        </Box> : null}

        {netZeroTargetSet && netZeroTargetSet === "false" ? <Box>
          <Flex alignItems="center">
            <EsgLabel text={`${t("futurePlans")}: *`} />
            <Tooltip
              label={t("requestToProvideDataTooltip", { sourcingCompany: data?.organizationName })}
              whiteSpace={"pre-line"}
              hasArrow
            >
              <IconButton
                aria-label="request-to-provide-data-tooltip"
                type="button"
                icon={<FaQuestionCircle size="20" />}
                flexShrink="0"
                size="sm"
                opacity="0.25"
                tabIndex={-1}
              />
            </Tooltip>
          </Flex>
          <FormSelect
            errors={formState.errors}
            {...register("futurePlans", {
              required: globalTranslation("errors.required")
            })}
            placeholder=" "
            title={futurePlans && futurePlans !== "" ? t(`options.${futurePlans}`) : ""}
            disabled={isPreview}
          >
            <option value={FUTURE_PLANS.IN_DEVELOPMENT}>{t(`options.${FUTURE_PLANS.IN_DEVELOPMENT}`)}</option>
            <option value={FUTURE_PLANS.NOT_STARTED}>{t(`options.${FUTURE_PLANS.NOT_STARTED}`)}</option>
            <option value={FUTURE_PLANS.OTHER}>{t(`options.${FUTURE_PLANS.OTHER}`)}</option>
          </FormSelect>
          <FormTextArea
            errors={formState.errors}
            {...register("futurePlansClarification", {
              required: netZeroTargetSet && netZeroTargetSet === "false" && futurePlans === FUTURE_PLANS.OTHER ?
                globalTranslation("errors.required") : false
            })}
            mt={2}
            maxHeight="50"
            width={"50%"}
            disabled={isPreview}
          />
        </Box> : null}

        <Box>
          <EsgLabel text={`${t("uploadReport")}:`} />
          <FileAttachment
            type={"sustainability-report"}
            uploadHook={useAttachLatestSustainabilityReport}
            downloadHook={useGetDecarbAttachment}
            deleteHook={useDeleteDecarbAttachment}
            invalidationKey={supplierQuestionnaireKeys.supplierQuestionnaireById()}
            currentAttachment={sustainabilityAttachment}
            params={{
              questionnaireId: questionnaireId
            }}
            isDisabled={isPreview}
            tooltip={t("fileAttachmentTooltip")}
            onSuccessHandler={(data) => {
              if (data !== null && typeof data === "object") {
                if (data.status === StatusCodes.NO_CONTENT) {
                  // Handling the case of successful file delete
                  setSustainabilityAttachment(null);
                } else {
                  // Handling the case of successful file upload
                  setSustainabilityAttachment({
                    attachmentName: data.name,
                    attachmentId: data.documentId
                  });
                }
              }
            }}
          />
        </Box>

        <Box>
          <EsgLabel text={`${t("uploadDeforestationPlan")}:`} />
          <FileAttachment
            type={"deforestation-plan"}
            uploadHook={useAttachDeforestationPlanDocumentation}
            downloadHook={useGetDecarbAttachment}
            deleteHook={useDeleteDecarbAttachment}
            invalidationKey={supplierQuestionnaireKeys.supplierQuestionnaireById()}
            currentAttachment={deforestationAttachment}
            params={{
              questionnaireId: questionnaireId
            }}
            isDisabled={isPreview}
            tooltip={t("fileAttachmentTooltip")}
            onSuccessHandler={(data) => {
              if (data !== null && typeof data === "object") {
                if (data.status === StatusCodes.NO_CONTENT) {
                  // Handling the case of successful file delete
                  setDeforestationAttachment(null);
                } else {
                  // Handling the case of successful file upload
                  setDeforestationAttachment({
                    attachmentName: data.name,
                    attachmentId: data.documentId
                  });
                }
              }
            }}
          />
        </Box>
      </Flex>

      {isPreview !== true ? <Flex
        justifyContent="end"
        marginBlockStart="10"
        position="sticky"
        bottom="0"
        backgroundColor="#fff"
        borderColor="esg_gray.100"
        bordersyle="solid"
        borderTopWidth="1px"
        paddingBlock="4"
        zIndex="10"
      >
        <Flex gap="2">
          <Button
            colorScheme="white"
            color="esg_lightgreen"
            border={"1px solid"}
            borderColor={"esg_lightgreen"}
            type="submit"
            width="32"
          >
            {globalTranslation("buttons.save")}
          </Button>
          <Button
            colorScheme="customScheme"
            width="32"
            onClick={() => {
              setFinalizeQuestionnaire(true);
              onConfirmDialogOpen();
            }}
          >
            {globalTranslation("buttons.finalize")}
          </Button>
        </Flex>
      </Flex> : null}

      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={globalTranslation("questionnaireFinalizationDialog.header")}
        confirmationText={globalTranslation("questionnaireFinalizationDialog.question")}
      />
    </chakra.form>
  );
}

export default DecarbonizationNetZeroPlan;